// ─────────────────────────────────────────────────────────────
// CloudflarePublish — Publish DAW project to Sunday Triggers library
//
// Flow:
//  1. Validate required Song Info fields
//  2. Read BPM + time signature from DAW engine
//  3. Read Group, GroupSustain, Section markers → build splitter markers
//  4. Render all stems via OfflineEngineRenderer (no dialog, include all)
//  5. Split every stem at every bar + every section boundary
//  6. Build raw JSON manifest (same structure as Base44 audioProcessor)
//  7. Pack all split WAV clips into one ZIP (JSZip)
//  8. Upload ZIP to st-audio R2 via st-daw-audio-publish worker
//  9. POST or PUT to st-library-api with all song data + file_storage_key + manifest
// 10. Write returned song ID back to project meta as st_db_id
// ─────────────────────────────────────────────────────────────

import {
    DefaultObservableValue,
    Errors,
    Option,
    RuntimeNotifier,
    unitValue,
    UUID
} from "@opendaw/lib-std"
import {AudioData, PPQN} from "@opendaw/lib-dsp"
import {ExternalLib, OfflineEngineRenderer} from "@opendaw/studio-core"
import {ExportStemsConfiguration} from "@opendaw/studio-adapters"
import {StudioService} from "@/service/StudioService"
import {jerryMarkerStore} from "@/jerry/markers/JerryMarkerStore"
import {STApi} from "@/jerry/st-panel/STApi"
import {STProjectMeta} from "@/jerry/st-panel/STTypes"
import {ClerkAuth} from "../auth/ClerkAuth"

// ── Types ─────────────────────────────────────────────────────

type SplitterMarker = {
    type: "group" | "groupSustain" | "section"
    beat: number   // beat position (0-based)
    name: string
}

type ChunkInfo = {
    randomFileName: string
    sourceFile: string
    order: number
    startTime: number
    endTime: number
    duration: number
    section: {id: string; name: string; group: string | null; loopSection: boolean} | null
    // True for the trailing reverb/decay tail clip (the audio past the musical
    // end). It is NOT part of the timed loop body — the app plays it as a
    // concurrent voice over the loop seam so reverb rings across the loop
    // without affecting timing.
    isTail?: boolean
}

// ── Constants ─────────────────────────────────────────────────

const AUDIO_WORKER_URL = "https://api.sundaytriggers.com/daw/audio"
const CHUNK_DURATION_BARS = 1
const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"

// ── Helpers ───────────────────────────────────────────────────

// Manifests are NO LONGER encrypted here. The DAW sends the plaintext
// manifest (as a JSON string) over its authenticated channel; st-library-api
// encrypts it at rest and seals it per device on delivery. There is no
// shared master key anymore.
function serializeManifest(data: object): string {
    return JSON.stringify(data)
}

function randomFilename(): string {
    let r = ""
    for (let i = 0; i < 8; i++) r += CHARS[Math.floor(Math.random() * CHARS.length)]
    return r
}

function generateSectionId(): string {
    return "sec_" + Math.random().toString(36).slice(2, 11)
}

async function authHeaders(): Promise<HeadersInit> {
    const token = await ClerkAuth.getToken()
    return {Authorization: `Bearer ${token}`}
}

// ── WAV encoder (matches Base44 audioBufferToWav exactly) ─────

function encodeWav(frames: Float32Array[], sampleRate: number, numFrames: number): ArrayBuffer {
    const numChannels = frames.length
    const length = numFrames * numChannels * 2
    const buffer = new ArrayBuffer(44 + length)
    const view = new DataView(buffer)
    let pos = 0
    const u16 = (v: number) => {view.setUint16(pos, v, true); pos += 2}
    const u32 = (v: number) => {view.setUint32(pos, v, true); pos += 4}
    u32(0x46464952); u32(36 + length); u32(0x45564157)
    u32(0x20746d66); u32(16); u16(1); u16(numChannels)
    u32(sampleRate); u32(sampleRate * numChannels * 2)
    u16(numChannels * 2); u16(16)
    u32(0x61746164); u32(length)
    for (let i = 0; i < numFrames; i++) {
        for (let ch = 0; ch < numChannels; ch++) {
            let s = Math.max(-1, Math.min(1, frames[ch][i]))
            s = s < 0 ? s * 0x8000 : s * 0x7fff
            view.setInt16(pos, s, true); pos += 2
        }
    }
    return buffer
}

// ── Build splitter markers from JerryMarkerStore ──────────────

function buildSplitterMarkers(_bpm: number): SplitterMarker[] {
    const data = jerryMarkerStore.exportForProject()
    const markers: SplitterMarker[] = []

    // PPQN.Quarter = 960 pulses per beat
    // beat = pulses / Quarter
    const pulsesToBeat = (pulses: number): number => pulses / PPQN.Quarter

    for (const m of data.groupMarkers) {
        markers.push({type: "group", beat: pulsesToBeat(m.position), name: m.label})
    }
    for (const m of data.groupSustainMarkers) {
        markers.push({type: "groupSustain", beat: pulsesToBeat(m.position), name: m.label})
    }
    for (const m of data.sectionMarkers) {
        markers.push({type: "section", beat: pulsesToBeat(m.position), name: m.label})
    }

    return markers.sort((a, b) => a.beat - b.beat)
}

// ── Audio splitting (mirrors Base44 audioProcessor.splitAudioBuffer) ──

function splitStem(
    _frames: Float32Array[],   // [left, right] for stereo — reserved for future per-chunk encode
    sampleRate: number,
    numFrames: number,
    bpm: number,
    beatsPerBar: number,
    markers: SplitterMarker[],
    musicalEndSeconds: number,
): ChunkInfo[] {
    const duration = numFrames / sampleRate
    const secondsPerBeat = 60 / bpm
    const barDuration = secondsPerBeat * beatsPerBar
    const chunkDurationSeconds = barDuration * CHUNK_DURATION_BARS

    // Build section definitions from markers
    let currentGroup: string | null = null
    let currentGroupShouldLoop = false
    const sectionDefinitions: Array<{barTime: number; name: string; group: string | null; loopSection: boolean; id: string}> = []

    for (const marker of markers) {
        if (marker.type === "group") {
            currentGroup = marker.name || null
            currentGroupShouldLoop = false
        } else if (marker.type === "groupSustain") {
            currentGroup = marker.name || null
            currentGroupShouldLoop = true
        } else if (marker.type === "section") {
            sectionDefinitions.push({
                barTime: marker.beat * secondsPerBeat,
                name: marker.name,
                group: currentGroup,
                loopSection: currentGroupShouldLoop,
                id: generateSectionId(),
            })
        }
    }

    sectionDefinitions.sort((a, b) => a.barTime - b.barTime)

    // ── OLD (kept for rollback) ───────────────────────────────────────────
    // What it did: built split points at every bar + every section, then cut
    // at the RAW rendered length (`duration`). Because the offline renderer
    // keeps rendering past the content until the sound decays to silence, that
    // `duration` included the last hit's decay tail past the final bar. Result:
    // an extra trailing clip, and — since the loop player plays clips back-to-
    // back — a loop longer than a whole bar, so looping drifted off-tempo.
    //
    // const splitTimes = new Set<number>([0])
    // for (let t = chunkDurationSeconds; t < duration; t += chunkDurationSeconds) splitTimes.add(t)
    // for (const sec of sectionDefinitions) {if (sec.barTime < duration) splitTimes.add(sec.barTime)}
    //
    // const sortedSplits = Array.from(splitTimes).sort((a, b) => a - b)
    // sortedSplits.push(duration)

    // ── NEW ───────────────────────────────────────────────────────────────
    // Cap splitting at the MUSICAL end (last region / bar grid), not the raw
    // rendered length. `musicalEndSeconds` is where the content actually ends;
    // everything after it is just the decay tail the renderer captured. Cutting
    // there means clips total an exact number of bars, so loops stay locked to
    // tempo, and the trailing tail is dropped instead of becoming its own clip.
    // Real content is never at risk: it always lives inside the region, so it
    // ends on/inside `musicalEndSeconds` — only the tail falls past it.
    const splitEnd = musicalEndSeconds > 0 ? Math.min(musicalEndSeconds, duration) : duration
    const splitTimes = new Set<number>([0])
    for (let t = chunkDurationSeconds; t < splitEnd; t += chunkDurationSeconds) splitTimes.add(t)
    for (const sec of sectionDefinitions) {if (sec.barTime < splitEnd) splitTimes.add(sec.barTime)}

    const sortedSplits = Array.from(splitTimes).sort((a, b) => a - b)
    sortedSplits.push(splitEnd)

    const chunks: ChunkInfo[] = []
    let currentSection: ChunkInfo["section"] = null
    let order = 1

    for (let i = 0; i < sortedSplits.length - 1; i++) {
        const startTime = sortedSplits[i]
        const endTime = Math.min(sortedSplits[i + 1], splitEnd)

        const sectionDef = sectionDefinitions.find(s => Math.abs(s.barTime - startTime) < 0.01)
        if (sectionDef) {
            currentSection = {
                id: sectionDef.id,
                name: sectionDef.name,
                group: sectionDef.group,
                loopSection: sectionDef.loopSection,
            }
        }

        const startSample = Math.floor(startTime * sampleRate)
        const endSample = Math.floor(endTime * sampleRate)
        const chunkLength = endSample - startSample
        if (chunkLength <= 0) continue

        chunks.push({
            randomFileName: randomFilename() + ".wav",
            sourceFile: "",          // filled in by caller
            order: order++,
            startTime,
            endTime,
            duration: endTime - startTime,
            section: currentSection ? {...currentSection} : null,
        })
    }

    // ── TAIL CLIP ─────────────────────────────────────────────────────────
    // The reverb/decay tail past the musical end (splitEnd → rendered end).
    // The body clips above stay capped on the bar grid (exact bars → loops on
    // tempo). The tail is emitted as ONE extra clip flagged isTail so the app
    // can ring it over the loop seam as a concurrent voice, without it taking a
    // slot in the timed loop. Skipped when there is no tail (content lands on
    // the bar). To disable the feature, remove this block (body is unaffected).
    if (musicalEndSeconds > 0 && duration - splitEnd > 0.001) {
        chunks.push({
            randomFileName: randomFilename() + ".wav",
            sourceFile: "",          // filled in by caller
            order: order++,
            startTime: splitEnd,
            endTime: duration,
            duration: duration - splitEnd,
            section: null,
            isTail: true,
        })
    }

    return chunks
}

// ── Extract a single stereo stem from AudioData ───────────────

function extractStemFrames(audioData: AudioData, stemIndex: number): [Float32Array, Float32Array] {
    return [audioData.frames[stemIndex * 2], audioData.frames[stemIndex * 2 + 1]]
}

// ── Main publish function ─────────────────────────────────────

export namespace CloudflarePublish {

    export const publish = async (service: StudioService): Promise<void> => {
        if (!service.hasProfile) {
            await RuntimeNotifier.info({headline: "Publish", message: "No project is open."})
            return
        }

        const profile = service.profile
        const meta = profile.meta as STProjectMeta & Record<string, unknown>

        // ── 1. Validate required fields ───────────────────────
        const title = meta.name as string | undefined
        const st = meta as STProjectMeta
        const playerType = st.st_playerType ?? "song"
        const isLoopBuilder = playerType === "loopBuilder"
        const loopBuilderSubType = st.st_loopBuilderSubType ?? "sample"
        const isSample = isLoopBuilder && loopBuilderSubType === "sample"
        const isFactory = isLoopBuilder && loopBuilderSubType === "factory"

        if (!title?.trim()) {
            await RuntimeNotifier.info({headline: "Publish", message: "Title is required."})
            return
        }

        // Song-specific validations (not needed for loop builder)
        if (!isLoopBuilder) {
            const key = st.st_key
            const stType = st.st_type
            if (key == null) {
                await RuntimeNotifier.info({headline: "Publish", message: "Musical key is required. Set it in the Song Info panel."})
                return
            }
            if (!stType) {
                await RuntimeNotifier.info({headline: "Publish", message: "Song type is required. Set it in the Song Info panel."})
                return
            }
        }

        const abortController = new AbortController()
        const progress = new DefaultObservableValue<unitValue>(0.0)
        const dialog = RuntimeNotifier.progress({
            headline: `Publishing "${title}"…`,
            progress,
            cancel: () => abortController.abort(),
        })

        try {
            const project = service.profile.project

            // ── 2. Read engine values ─────────────────────────
            const bpm = Math.round(project.engine.bpm.getValue())
            const timeSignatureTop = project.timelineBox.signature.nominator.getValue()
            const timeSignatureBottom = project.timelineBox.signature.denominator.getValue()

            // ── 3. Build splitter markers ─────────────────────
            const markers = buildSplitterMarkers(bpm)
            dialog.message = "Preparing markers…"
            progress.setValue(0.05)

            // ── 4. Build stems config (stems for Factory/Song, mixdown for Sample) ─
            //
            // Sample sub-type → pass Option.none() so the renderer produces a
            // single stereo mixdown of all tracks (the "sample" the user records).
            // Factory sub-type and regular songs → pass per-track stems config.

            let stemsConfig: Option<ExportStemsConfiguration>
            let trackNames: string[]

            if (isSample) {
                // Mixdown — single stereo output, no per-stem config needed
                stemsConfig = Option.None
                trackNames = ["Mixdown"]
            } else {
                // Stems (Factory or regular Song)
                const config: ExportStemsConfiguration = Object.fromEntries(
                    project.rootBoxAdapter.audioUnits.adapters()
                        .filter(unit => !unit.isOutput)
                        .map(unit => [
                            UUID.toString(unit.uuid),
                            {
                                includeAudioEffects: true,
                                includeSends: true,
                                useInstrumentOutput: false,
                                fileName: unit.input.label.unwrap(),
                            } satisfies ExportStemsConfiguration[string],
                        ])
                ) as ExportStemsConfiguration

                stemsConfig = Option.wrap(config)
                trackNames = project.rootBoxAdapter.audioUnits.adapters()
                    .filter(unit => !unit.isOutput)
                    .map(unit => unit.input.label.unwrap())
            }

            // ── 5. Render ─────────────────────────────────────
            dialog.message = isSample ? "Rendering mixdown…" : "Rendering stems…"
            progress.setValue(0.1)

            await service.audioContext.suspend()
            const audioData: AudioData = await OfflineEngineRenderer.start(
                project.copy(),
                stemsConfig,
                progress,
                abortController.signal
            )
            await service.audioContext.resume()

            const numStems = audioData.numberOfChannels >> 1
            const sampleRate = audioData.sampleRate
            const numFrames = audioData.numberOfFrames
            const totalDurationSeconds = numFrames / sampleRate

            // Musical content end (last region / bar grid) in seconds — the same
            // boundary the renderer keys off (OfflineEngineRenderer uses
            // source.lastRegionAction()). splitStem caps splitting here so the
            // decay tail the renderer captures past the content isn't turned
            // into an extra trailing clip and loops stay exactly on-bar.
            const musicalEndSeconds = project.tempoMap.ppqnToSeconds(project.lastRegionAction())

            progress.setValue(0.4)
            dialog.message = "Splitting audio…"

            // ── Factory Preset path ───────────────────────────
            // Each stem → its own loop_builder_items record (own zip + manifest).
            // Then one factory_presets record links them all via channel sample_ids.
            if (isFactory) {
                const channelSampleIds: {channelIndex: number; sample_id: string}[] = []
                const factoryPresetId = `fp-${UUID.toString(profile.uuid)}`

                for (let s = 0; s < numStems; s++) {
                    const trackName = trackNames[s] ?? `Track ${s + 1}`
                    const stemId = `${UUID.toString(profile.uuid)}-s${s}`
                    const [left, right] = extractStemFrames(audioData, s)

                    dialog.message = `Processing stem ${s + 1}/${numStems}: ${trackName}…`
                    progress.setValue(0.4 + (s / numStems) * 0.35)

                    // Split this stem
                    const stemChunks = splitStem(
                        [left, right], sampleRate, numFrames, bpm, timeSignatureTop, markers, musicalEndSeconds
                    )
                    for (const chunk of stemChunks) chunk.sourceFile = trackName

                    // Build + encrypt manifest for this stem only
                    const stemManifest = {
                        version: "2.0",
                        created: new Date().toISOString(),
                        chunks: stemChunks.map(c => ({
                            file: c.randomFileName,
                            sourceFile: c.sourceFile,
                            order: c.order,
                            startTime: c.startTime,
                            endTime: c.endTime,
                            duration: c.duration,
                            section: c.section,
                            ...(c.isTail ? {isTail: true} : {}),
                        })),
                    }
                    const stemEncryptedManifest = serializeManifest(stemManifest)

                    // Pack this stem's chunks into its own zip
                    const stemLibResult = await ExternalLib.JSZip()
                    if (stemLibResult.status === "rejected") {
                        throw new Error(`Could not load JSZip: ${String(stemLibResult.error)}`)
                    }
                    const stemZip = new stemLibResult.value()

                    for (const chunk of stemChunks) {
                        const startSample = Math.floor(chunk.startTime * sampleRate)
                        const endSample = Math.floor(chunk.endTime * sampleRate)
                        const len = endSample - startSample
                        const cleft = left.slice(startSample, startSample + len)
                        const cright = right.slice(startSample, startSample + len)
                        stemZip.file(chunk.randomFileName, encodeWav([cleft, cright], sampleRate, len))
                    }

                    const stemZipBlob: Blob = await stemZip.generateAsync({
                        type: "blob",
                        compression: "DEFLATE",
                        compressionOptions: {level: 6},
                    })

                    // Upload stem zip to R2
                    const stemZipKey = `loop-builder/factory/${UUID.toString(profile.uuid)}/stem-${s}-${Date.now()}.zip`
                    const stemUploadUrl = `${AUDIO_WORKER_URL}/upload?key=${encodeURIComponent(stemZipKey)}`
                    const stemRes = await fetch(stemUploadUrl, {
                        method: "PUT",
                        headers: {...await authHeaders(), "Content-Type": "application/zip"},
                        body: await stemZipBlob.arrayBuffer(),
                    })
                    if (!stemRes.ok) {
                        const text = await stemRes.text()
                        throw new Error(`Stem ${s + 1} upload failed: ${stemRes.status} ${text}`)
                    }
                    const {file_storage_key: stemKey} = await stemRes.json() as {file_storage_key: string}

                    // Create or update loop_builder_items record for this stem
                    const stemPayload = {
                        id: stemId,
                        title: trackName,
                        bpm,
                        timeSignatureTop,
                        timeSignatureBottom,
                        timeSignature: [],
                        categories: st.st_categories ?? [],
                        tags: st.st_tags ?? [],
                        file_storage_key: stemKey,
                        is_premium: st.st_is_premium ?? false,
                        isPreviewable: false,
                        status: "published",
                        subType: "sample",
                        encrypted_manifest: stemEncryptedManifest,
                    }

                    try {
                        await STApi.updateLoopBuilder(stemId, stemPayload)
                    } catch (err: unknown) {
                        if (err instanceof Error && err.message.includes("404")) {
                            await STApi.createLoopBuilder(stemPayload)
                        } else {
                            throw err
                        }
                    }

                    channelSampleIds.push({channelIndex: s + 1, sample_id: stemId})
                }

                // POST or PUT the factory_presets record
                progress.setValue(0.9)
                dialog.message = "Saving factory preset…"

                const fpPayload = {
                    id: factoryPresetId,
                    name: title.trim(),
                    targetBpm: bpm,
                    categories: st.st_categories ?? [],
                    channels: channelSampleIds.map(ch => ({
                        channelIndex: ch.channelIndex,
                        sample_id: ch.sample_id,
                        rate: 1,
                        volume: 1,
                        mute: false,
                    })),
                    created_by_id: UUID.toString(profile.uuid),
                    created_by: title.trim(),
                }

                try {
                    await STApi.updateFactoryPreset(factoryPresetId, fpPayload)
                } catch (err: unknown) {
                    if (err instanceof Error && err.message.includes("404")) {
                        await STApi.createFactoryPreset(fpPayload)
                    } else {
                        throw err
                    }
                }

                ;(meta as Record<string, unknown>).st_db_id = factoryPresetId
                await service.projectProfileService.save()

                progress.setValue(1.0)
                dialog.message = "Published!"
                await RuntimeNotifier.info({
                    headline: "Published",
                    message: `"${title}" has been published as a Factory Preset.`,
                })
                return
            }

            // ── 6. Split every stem, build manifest ───────────
            const allChunks: ChunkInfo[] = []

            for (let s = 0; s < numStems; s++) {
                const trackName = trackNames[s] ?? `Track ${s + 1}`
                const [left, right] = extractStemFrames(audioData, s)
                const stemChunks = splitStem([left, right], sampleRate, numFrames, bpm, timeSignatureTop, markers, musicalEndSeconds)

                for (const chunk of stemChunks) {
                    chunk.sourceFile = trackName
                    allChunks.push(chunk)
                }
            }

            progress.setValue(0.5)
            dialog.message = "Building manifest…"

            const manifest = {
                version: "2.0",
                created: new Date().toISOString(),
                chunks: allChunks.map(c => ({
                    file: c.randomFileName,
                    sourceFile: c.sourceFile,
                    order: c.order,
                    startTime: c.startTime,
                    endTime: c.endTime,
                    duration: c.duration,
                    section: c.section,
                    ...(c.isTail ? {isTail: true} : {}),
                })),
            }

            dialog.message = "Encrypting manifest…"
            const encryptedManifest = serializeManifest(manifest)
            progress.setValue(0.53)

            // ── 7. Pack ZIP ───────────────────────────────────
            dialog.message = "Creating ZIP…"
            progress.setValue(0.55)

            const libResult = await ExternalLib.JSZip()
            if (libResult.status === "rejected") {
                throw new Error(`Could not load JSZip: ${String(libResult.error)}`)
            }
            const zip = new libResult.value()

            // Encode each chunk to WAV and add to zip
            for (let s = 0; s < numStems; s++) {
                const [left, right] = extractStemFrames(audioData, s)
                const trackName = trackNames[s] ?? `Track ${s + 1}`
                const stemChunks = allChunks.filter(c => c.sourceFile === trackName)

                for (const chunk of stemChunks) {
                    const startSample = Math.floor(chunk.startTime * sampleRate)
                    const endSample = Math.floor(chunk.endTime * sampleRate)
                    const len = endSample - startSample
                    const cleft = left.slice(startSample, startSample + len)
                    const cright = right.slice(startSample, startSample + len)
                    const wavBuffer = encodeWav([cleft, cright], sampleRate, len)
                    zip.file(chunk.randomFileName, wavBuffer)
                }
            }

            const zipBlob: Blob = await zip.generateAsync({
                type: "blob",
                compression: "DEFLATE",
                compressionOptions: {level: 6},
            }, (meta: {percent: number}) => progress.setValue(0.55 + meta.percent * 0.001 * 0.15))

            progress.setValue(0.7)

            // ── 8. Upload ZIP to R2 ───────────────────────────
            dialog.message = "Uploading to cloud…"

            // Timestamp in the key ensures each publish gets a unique path.
            // This means Cloudflare's CDN cache never serves a stale version
            // after a re-publish — no cache purge API needed.
            const exportLabel = isSample ? "mixdown" : "stems"
            const zipKey = `projects/${UUID.toString(profile.uuid)}/${title.replace(/\s+/g, "-").toLowerCase()}-${Date.now()}-${exportLabel}.zip`
            const uploadUrl = `${AUDIO_WORKER_URL}/upload?key=${encodeURIComponent(zipKey)}`

            const zipArrayBuffer = await zipBlob.arrayBuffer()
            const uploadRes = await fetch(uploadUrl, {
                method: "PUT",
                headers: {...await authHeaders(), "Content-Type": "application/zip"},
                body: zipArrayBuffer,
            })
            if (!uploadRes.ok) {
                const text = await uploadRes.text()
                throw new Error(`Upload failed: ${uploadRes.status} ${text}`)
            }
            const {file_storage_key} = await uploadRes.json() as {file_storage_key: string}

            progress.setValue(0.85)
            dialog.message = "Saving to library…"

            // ── 9. Write to database ──────────────────────────
            const existingId = st.st_db_id
            let savedRecord: {id: string}

            if (isLoopBuilder) {
                // ── Loop Builder payload ──────────────────────
                const lbPayload = {
                    id: UUID.toString(profile.uuid),
                    title: title.trim(),
                    bpm,
                    timeSignatureTop,
                    timeSignatureBottom,
                    timeSignature: (timeSignatureTop && timeSignatureBottom)
                        ? [{
                            id:   `${timeSignatureTop}/${timeSignatureBottom}`,
                            name: `${timeSignatureTop}/${timeSignatureBottom}`,
                        }]
                        : [],
                    categories: st.st_categories ?? [],
                    tags: st.st_tags ?? [],
                    file_storage_key,
                    image_url: st.st_image_url ?? undefined,
                    is_premium: st.st_is_premium ?? false,
                    isPreviewable: st.st_isPreviewable ?? false,
                    status: st.st_status ?? "draft",
                    subType: loopBuilderSubType,
                    encrypted_manifest: encryptedManifest,
                }

                if (existingId) {
                    try {
                        savedRecord = await STApi.updateLoopBuilder(existingId, lbPayload)
                    } catch (err: unknown) {
                        if (err instanceof Error && err.message.includes("404")) {
                            savedRecord = await STApi.createLoopBuilder(lbPayload)
                        } else {
                            throw err
                        }
                    }
                } else {
                    savedRecord = await STApi.createLoopBuilder(lbPayload)
                }
            } else {
                // ── Resolve fresh type and categories from localStorage ──
                // The project meta holds a snapshot taken when the user last
                // selected the type/category. If they later edited the type
                // (e.g. toggled showInFilter or changed the icon) without
                // re-selecting, the snapshot is stale. Always look up the
                // current version by ID so edits are never silently dropped.
                const freshType = st.st_type?.id
                    ? (STApi.getTypes().find(t => t.id === st.st_type!.id) ?? st.st_type)
                    : st.st_type

                const freshCategories = (st.st_categories ?? []).map(cat =>
                    STApi.getCategories().find(c => c.id === cat.id) ?? cat
                )

                // Push type and each category to the API as independent
                // entities so all songs sharing them stay in sync automatically.
                if (freshType) {
                    await STApi.upsertType(freshType)
                }
                for (const cat of freshCategories) {
                    await STApi.upsertCategory(cat)
                }

                // ── Song payload ──────────────────────────────
                const songPayload = {
                    id: UUID.toString(profile.uuid),
                    title: title.trim(),
                    artist: (meta.artist as string | undefined) ?? undefined,
                    type: freshType ?? undefined,
                    type_id: freshType?.id ?? undefined,
                    categories: freshCategories,
                    duration: Math.round(totalDurationSeconds),
                    key: st.st_key,
                    octaveUp: st.st_octaveUp ?? [],
                    octaveDown: st.st_octaveDown ?? [],
                    bpm,
                    timeSignatureTop,
                    timeSignatureBottom,
                    file_storage_key,
                    image_url: st.st_image_url ?? undefined,
                    is_premium: st.st_is_premium ?? false,
                    isPreviewable: st.st_isPreviewable ?? false,
                    tags: st.st_tags ?? [],
                    status: st.st_status ?? "draft",
                    playerType,
                    preacherCompanion: st.st_preacherCompanion ?? null,
                    isHidden: st.st_isHidden ?? false,
                    isPreachModeFeatured: st.st_isPreachModeFeatured ?? false,
                    encrypted_manifest: encryptedManifest,
                }

                if (existingId) {
                    try {
                        savedRecord = await STApi.updateSong(existingId, songPayload)
                    } catch (err: unknown) {
                        if (err instanceof Error && err.message.includes("404")) {
                            savedRecord = await STApi.createSong(songPayload)
                        } else {
                            throw err
                        }
                    }
                } else {
                    savedRecord = await STApi.createSong(songPayload)
                }
            }

            // ── 10. Save DB id back to project (explicit save so it persists) ──
            ;(meta as Record<string, unknown>).st_db_id = savedRecord.id
            await service.projectProfileService.save()

            // Notify any listeners (e.g. NoFeaturedPreacherBanner) that
            // published content has changed so they can refresh.
            document.dispatchEvent(new CustomEvent("st:published"))

            progress.setValue(1.0)
            dialog.message = "Published!"

            const typeLabel = isLoopBuilder ? "Loop Builder" : "Song"
            await RuntimeNotifier.info({
                headline: "Published",
                message: `"${title}" has been published to the ${typeLabel} library.`,
            })

        } catch (reason: unknown) {
            if (!Errors.isAbort(reason)) {
                console.warn("[CloudflarePublish]", reason)
                await RuntimeNotifier.info({
                    headline: "Publish Failed",
                    message: String(reason),
                })
            }
        } finally {
            progress.terminate()
            dialog.terminate()
        }
    }
}
